import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/toast/toast-provider";

const MAX_MESSAGE_LENGTH = 200;

const isDisplayable = (value: unknown): value is string =>
	typeof value === "string" &&
	value.length > 0 &&
	value.length <= MAX_MESSAGE_LENGTH;

/**
 * Pick the message to toast for a failed query.
 *
 * Only server-authored text is surfaced verbatim: the API's error envelope
 * (`resperr.ErrResponse` → `{ "message": ... }`) and the `msg` field older hooks
 * attach. `AxiosError.message` is deliberately NOT surfaced — it is a
 * library-internal English string ("Request failed with status code 404") that
 * has no translation and means nothing to a user, so an axios failure falls
 * through to the localized generic message instead.
 */
const sanitizeErrorMessage = (error: unknown, fallback: string): string => {
	if (typeof error === "object" && error !== null) {
		const err = error as Record<string, any>;

		const serverMessage = err.response?.data?.message ?? err.msg;
		if (isDisplayable(serverMessage)) return serverMessage;

		if (!err.isAxiosError && isDisplayable(err.message)) return err.message;
	}

	return fallback;
};

export const QueryErrorHandler = () => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { t } = useTranslation("common");
	const toastRef = useRef(toast);
	toastRef.current = toast;
	// Read through a ref so switching language doesn't tear down and re-create
	// the cache subscription.
	const tRef = useRef(t);
	tRef.current = t;

	useEffect(() => {
		const cache = queryClient.getQueryCache();
		const unsubscribe = cache.subscribe((event) => {
			if (event?.type === "updated" && event.action?.type === "error") {
				const error = event.action.error as Record<string, any>;
				if (error?.response?.status === 401) return;
				toastRef.current.pushError(
					sanitizeErrorMessage(error, tRef.current("states.error")),
				);
			}
		});
		return unsubscribe;
	}, [queryClient]);

	return null;
};
